import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// --- Generic keyboard shortcuts hook ---

type ShortcutHandler = () => void;

interface Shortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  handler: ShortcutHandler;
  description: string;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts(shortcuts: Shortcut[]) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;

      for (const shortcut of shortcuts) {
        if (
          e.key.toLowerCase() === shortcut.key.toLowerCase() &&
          !!e.ctrlKey === !!shortcut.ctrl &&
          !!e.shiftKey === !!shortcut.shift
        ) {
          e.preventDefault();
          shortcut.handler();
          return;
        }
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [shortcuts]);
}

// --- Two-key "go" navigation shortcuts ---
// Press `g` then a letter within 500ms to navigate to a route.

const GO_ROUTES: Record<string, string> = {
  h: '/',             // home / dashboard
  c: '/contacts',     // contacts
  e: '/email',        // email
  s: '/support',      // support
  f: '/finance',      // finance
  p: '/partnerships', // partnerships
  r: '/growth',       // growth (r for revenue)
  m: '/messaging',    // messaging
  v: '/voice',        // voice
  x: '/context',      // context explorer
  t: '/settings',     // settings (t for tuning)
  a: '/audit',        // audit
};

export function useGoShortcuts() {
  const navigate = useNavigate();
  const pending = useRef(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;

      if (e.key === 'g' && !e.ctrlKey && !e.metaKey) {
        pending.current = true;
        setTimeout(() => {
          pending.current = false;
        }, 500);
        return;
      }

      if (pending.current) {
        pending.current = false;
        const route = GO_ROUTES[e.key];
        if (route) {
          e.preventDefault();
          navigate(route);
        }
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [navigate]);
}

// --- Shortcut help (? key) ---
// Logs available shortcuts to console. A full modal can replace this later.

export function useShortcutHelp() {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;

      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        console.group('%c Keyboard Shortcuts', 'font-weight:bold;font-size:14px');
        console.log('%cNavigation (g + key):', 'font-weight:bold');
        for (const [key, route] of Object.entries(GO_ROUTES)) {
          console.log(`  g ${key}  →  ${route}`);
        }
        console.log('%cGlobal:', 'font-weight:bold');
        console.log('  ?    →  Show this help');
        console.groupEnd();
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);
}
